import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

type Theme = 'light' | 'dark';

interface ThemeContextType {
  theme: Theme;
  isDark: boolean;
  isLight: boolean;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
  // Helper function to get theme-aware class names
  c: (lightClass: string, darkClass: string) => string;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
  initialTheme?: Theme;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children, initialTheme }) => {
  const [theme, setThemeState] = useState<Theme>(() => {
    // Check body class first (set by App.tsx)
    if (typeof document !== 'undefined') {
      return document.body.classList.contains('theme-day') ? 'light' : 'dark';
    }
    return initialTheme || 'light';
  });

  // Sync with body class changes
  useEffect(() => {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          const isDayTheme = document.body.classList.contains('theme-day');
          setThemeState(isDayTheme ? 'light' : 'dark');
        }
      });
    });

    observer.observe(document.body, { attributes: true });

    // Initial sync
    const isDayTheme = document.body.classList.contains('theme-day');
    setThemeState(isDayTheme ? 'light' : 'dark');

    return () => observer.disconnect();
  }, []);

  const setTheme = (newTheme: Theme) => {
    setThemeState(newTheme);
    if (newTheme === 'light') {
      document.body.classList.add('theme-day');
    } else {
      document.body.classList.remove('theme-day');
    }
  };

  const toggleTheme = () => {
    setTheme(theme === 'light' ? 'dark' : 'light');
  };

  // Helper function to pick class based on theme
  const c = (lightClass: string, darkClass: string): string => {
    return theme === 'light' ? lightClass : darkClass;
  };

  const value: ThemeContextType = {
    theme,
    isDark: theme === 'dark',
    isLight: theme === 'light',
    setTheme,
    toggleTheme,
    c,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

// Standalone hook that doesn't require context (for simpler usage)
export const useThemeClass = () => {
  const [isLight, setIsLight] = useState(() => {
    if (typeof document !== 'undefined') {
      return document.body.classList.contains('theme-day');
    }
    return true;
  });

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsLight(document.body.classList.contains('theme-day'));
    });

    observer.observe(document.body, { attributes: true });
    setIsLight(document.body.classList.contains('theme-day'));

    return () => observer.disconnect();
  }, []);

  return {
    isLight,
    isDark: !isLight,
    theme: isLight ? 'light' as const : 'dark' as const,
    c: (lightClass: string, darkClass: string) => isLight ? lightClass : darkClass,
  };
};

export default ThemeContext;
